import { useAuthStore } from '~/store/auth';

// Auth endpoints must never trigger a refresh-and-retry: /refresh failing IS the signal that the
// session is gone, and the login/exchange routes mint the very token we'd be refreshing. Retrying
// any of them recurses, because auth.refresh() builds its own gateway client with these same
// interceptors.
const NON_REFRESHABLE_PATHS = [
  '/v1/auth/refresh',
  '/v1/auth/login',
  '/v1/auth/logout',
  '/v1/auth/mfa/',
  '/v1/auth/shopify-login/exchange',
  // stop/current are @Public server-side, so a 401 from them is not a session-expiry signal.
  '/v1/auth/impersonation/',
  '/v1/fluid/dri/exchange',
];

// A dashboard user whose access token expired refreshes and replays ('refreshed'); a user whose
// session is genuinely dead is logged out ('failed'); an unauthenticated visitor on the login, MFA,
// or set-password pages never had a session to end, so their 401s must simply propagate
// ('no-session') instead of yanking them to /login mid-flow (ENG-5882).
export type RefreshOutcome = 'refreshed' | 'failed' | 'no-session';

// useGateway() constructs a fresh service (and axios instance) per call, so this coordinator has to
// live at module scope. A dashboard mount fires many requests at once; without it, every one of
// them would 401 and kick off its own refresh, and each rotated refresh token would invalidate the
// one the others are holding.
let inFlight: Promise<RefreshOutcome> | null = null;

export function isRefreshablePath(url?: string): boolean {
  if (!url) return true;

  return !NON_REFRESHABLE_PATHS.some(path => url.includes(path));
}

// auth.refresh() resolves to a truthy envelope even when it fails — a 401 from /v1/auth/refresh is
// swallowed into `{ data: null }` by the gateway's own unauthorized handler, and its catchError
// path returns `of(null)` wrapped the same way. So success is measured by what landed in the store,
// not by what the call returned.
async function runRefresh(): Promise<RefreshOutcome> {
  const auth = useAuthStore();
  const previous = auth.accessToken;

  // Captured before refresh(): its no-refresh-token short-circuit calls clearUser(), which erases
  // exactly the evidence this decision needs. Any of the three counts, so a logged-in user is
  // logged out cleanly rather than stranded on silently-empty pages — the cookies can disappear
  // from under a live tab (ITP evicts these script-written sameSite=none cookies after 7 days),
  // and the in-memory token proves this tab authenticated. None of them is set on a rendered
  // /login/* page: accessToken is per-tab and only written by handleAuthResponse, which the
  // mfaRequired branch never reaches, and login.vue bounces cookie-authenticated visitors away
  // before any child page mounts.
  const hadSession = !!auth.refreshToken || !!auth.authenticated || !!auth.accessToken;

  try {
    await auth.refresh();
  } catch {
    return hadSession ? 'failed' : 'no-session';
  }

  if (auth.accessToken && auth.accessToken !== previous) {
    return 'refreshed';
  }

  return hadSession ? 'failed' : 'no-session';
}

export function refreshAccessToken(): Promise<RefreshOutcome> {
  if (inFlight) return inFlight;

  inFlight = runRefresh().finally(() => {
    inFlight = null;
  });

  return inFlight;
}

// Called from an axios interceptor, which is outside any component setup, so the Nuxt instance
// backing navigateTo() isn't guaranteed to be reachable. Falling back to a full page load is fine
// here — the session is already gone, so there is no client state left worth preserving.
export async function endSession(): Promise<void> {
  const auth = useAuthStore();
  auth.clearUser();

  // Already inside the login flow (MFA, set-password): navigating to /login would wipe the
  // visitor's progress and query params, and there is no client state left worth protecting.
  // `window` is safe unguarded — endSession is only reachable from browser-side axios interceptors.
  if (window.location.pathname.startsWith('/login')) return;

  try {
    await navigateTo('/login');
  } catch {
    window.location.assign('/login');
  }
}

export function resetRefreshState(): void {
  inFlight = null;
}
